import React, { useEffect, useState, useCallback } from 'react';

import Icon from '../../Icon2/Icon'
import Tooltip from '../../Tooltip/Tooltip';
import Highlighted from '../util/Highlighted';
import { SkeletonLoader } from '../util/SkeletonLoader';

export const getTestIdFormatter = (testIdString: string): string => {
  if (testIdString) {
    testIdString = testIdString.trim().split(" ").join("-").toLowerCase();
    return testIdString
  }
  return testIdString
}

const HighlightAndTruncate = ({ label, search, testId, onClick }: any) => {
  return (
    <div onClick={onClick} className="middle-truncate">
      <Tooltip content={label} position="top" disabled={label.length <= 23}>
        <div data-test-id={testId} className="middle-truncate-start">
          <Highlighted text={label} highlight={search} testId={testId + '-label'} />
        </div>
      </Tooltip>
    </div>
  );
};

const useOnScreen = ({ root = null, rootMargin = '0px', threshold = 0 } = {}) => {
  const [observer, setOserver] = useState();
  const [isIntersecting, setIntersecting] = useState(false);

  const measureRef = useCallback(
    (node) => {
      if (node) {
        const observer: any = new IntersectionObserver(
          ([entry]) => {
            setIntersecting(entry.isIntersecting);
          },
          { root, rootMargin, threshold }
        );

        observer.observe(node);
        setOserver(observer);
      }
    },
    [root, rootMargin, threshold]
  );

  return { measureRef, isIntersecting, observer };
};

const HeaderComponent = ({ node, userSelections, searchInput, handleSelectQuery }: any) => {
  const { updateMultipleValue, filterObject, clearSelection } = userSelections;

  const { measureRef, isIntersecting, observer }: any = useOnScreen();

  useEffect(() => {
    if (isIntersecting) {
      node?.onClick();
      observer.disconnect();
    }
  }, [isIntersecting]);

  const handleClick = (e): void => {
    e.stopPropagation();
    e.preventDefault();
    handleSelectQuery('taxonomyTermLabel', { label: node.name, value: [{ "taxonomy_uid": node.taxonomyUid, "term_uid": node.uid }] })
    let selectedData = { label: node.name, value: node.uid, taxonomy_uid: node.taxonomyUid };
    node.checked = !node.checked;
  };

  const disableClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  if (node && node.name === 'rootNode') {
    return null;
  }

  if (node && node.id === 'loadmore') {
    return (
      <div ref={measureRef}>
        <SkeletonLoader size={5} />
      </div>
    );
  }

  if (node.group && node.search) {
    return (
      <div
        className="tableFilterModal__suggestion-items--title flex-v-center"
        onClick={disableClick}
        data-test-id="cs-taxonomy-filter-result-title">

        <HighlightAndTruncate label={node.name} search={searchInput} testId="cs-taxonomy-filter-result-title-label" />

      </div>
    );
  }

  const isNodeSelected = filterObject.value.find((fi) => fi.value == node.uid);
  if (typeof isNodeSelected === 'undefined') {
    node.checked = false;
  }

  if (node.search) {
    return (
      <div
        className="tableFilterModal__suggestion-item-result flex-v-center"
        onClick={disableClick}
        data-test-id="cs-taxonomy-filter-result-items">

        <div className="flex-column tableFilterModal__suggestion-item-result-element" onClick={handleClick}>
          <HighlightAndTruncate
            label={node.name}
            search={searchInput}
            testId={`cs-taxonomy-${getTestIdFormatter(node.name)}-label-text`}
          />
          <HighlightAndTruncate
            label={node.path}
            search={searchInput}
            testId={`cs-taxonomy-${getTestIdFormatter(node.path)}-label-path`}
          />

        </div>
      </div>
    );
  }

  if (node.type === 'blank') {
    return (
      <div
        className="tableFilterModal__suggestion-item"
        onClick={disableClick}
        data-test-id="cs-taxonomy-filter-item-blank">
        <div style={{ marginLeft: `${node.depth * 32}px` }} className="flex-v-center">
          <HighlightAndTruncate
            onClick={() => handleSelectQuery('taxonomyTermLabel', { label: node.name, value: [node.uid] })}
            label={node.name}
            search={searchInput}
            testId={`cs-taxonomy-${getTestIdFormatter(node.name)}-label-text`}
          />
        </div>
      </div>
    );
  }

  if (node.taxonomy) {
    const allSelectedTaxonomy = filterObject.value.filter((fi) => fi.taxonomy_uid == node.uid);
    return (
      <div
        className="tableFilterModal__suggestion-item tableFilterModal__suggestion-item-taxonomy tableFilterModal__suggestion-item-taxonomy-header"
        onClick={disableClick}
        data-test-id={`cs-filter-taxonomy-${getTestIdFormatter(node.name)}`}
      >
        <div
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation()
              e.preventDefault()
              node.onToggle(e);
            }
          }}
          tabIndex={0}
          className="flex-v-center tableFilterModal__suggestion-item-taxonomy--selectable"
          style={{ marginLeft: `${node.depth * 32 - 12}px` }}
          onClick={node.onToggle}>
          <div className="chevron-wrapper" data-test-id="cs-taxonomy-filter-toggle">
            <Icon className={`chevron ${node.toggled ? '' : 'close'}`} icon="Chevron" version="v2" />
          </div>
          <div
            // onClick={node.onToggle}
            className={`tableFilterModal__suggestion-item--element flex-v-center ${allSelectedTaxonomy.length > 0 && 'tableFilterModal__suggestion-item__highlight'
              }`}>
            <HighlightAndTruncate
              label={node.name}
              search={searchInput}
              testId={`cs-taxonomy-${getTestIdFormatter(node.name)}-label-text`}
            />
            <div className="ml-2" data-test-id="cs-taxonomy-filter-selected-items-count">
              {allSelectedTaxonomy.length > 0 && `(${allSelectedTaxonomy.length})`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (node.term) {
    return (
      <div
        className={`tableFilterModal__suggestion-item tableFilterModal__suggestion-item-taxonomy ${node?.checkedCount > 0 && !node.toggled && 'tableFilterModal__suggestion-item__highlight'
          }`}
        onClick={disableClick}
      >
        <div
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation()
              e.preventDefault()
              handleSelectQuery('taxonomyTermLabel', { label: node.name, value: [{ "taxonomy_uid": node.taxonomyUid, "term_uid": node.uid }] });
            }
          }}
          style={{ marginLeft: `${(node.depth * 28) - 16}px` }}
          className="flex-v-center tableFilterModal__suggestion-item-taxonomy--selectable"
          data-test-id="cs-taxonomy-filter-element"
          onClick={() => handleSelectQuery('taxonomyTermLabel', { label: node.name, value: [{ "taxonomy_uid": node.taxonomyUid, "term_uid": node.uid }] })}
        >
          {node.childrenCount > 0 ? (
            <div className="chevron-wrapper" onClick={node.onToggle} data-test-id="cs-taxonomy-filter-element-toggle">
              <Icon className={`chevron ${node.toggled ? '' : 'close'}`} icon="Chevron" version="v2" />
            </div>
          ) : <div className="ellipse" >
            <Icon icon="Ellipse" version="v2" />
          </div>}

          <HighlightAndTruncate
            label={node.name}
            search={searchInput}
            testId={`cs-taxonomy-${getTestIdFormatter(node.name)}-label-text`}
          />
        </div>
      </div>
    );
  }

  return null;
};

export default HeaderComponent;
